// ============================================================================
// Plan data, the billing-cycle toggle, and the card grid — used by the
// /pricing page (Pricing.tsx), with the plan data also read by the
// dashboard (Dashboard.tsx). One source of plan data so the screens can't
// quietly drift apart — there used to be separate hardcoded copies, which
// is exactly what went stale. Kept in its own file, rather than living in Pricing.tsx
// and having Dashboard.tsx import from there, so nothing here creates a
// circular import: Pricing.tsx imports from Dashboard.tsx (useAccount) as
// well as from here, so Dashboard.tsx importing back from Pricing.tsx
// would have made a cycle.
//
// Four plans (Free, Basic, Professional, Enterprise — names only borrowed
// from a reference competitor's pricing page, not their design or copy).
//
// YEARLY IS REAL: each paid tier has its own Dodo product, charging 12x
// its discounted per-month rate — $228, $708 and $1,788 a year. The
// checkout links are tagged with the tier, so the webhook grants exactly
// what was bought (server/src/billing/index.ts) and the limits below are
// enforced per tier (server/src/billing/entitlements.ts's PLAN_LIMITS —
// these two lists must stay in step, since the card is what the customer
// was shown when they paid).
//
// MONTHLY IS REAL TOO: each tier also has a monthly product. A plan with
// no product for a cycle gets a disabled Subscribe button and a note
// pointing at the other cycle, rather than a link to a differently-priced
// product — a card's price must never open a checkout that charges
// something else.

import { useEffect, useState } from 'preact/hooks';

export type Billing = 'monthly' | 'yearly';

export interface Plan {
  id: string;
  name: string;
  /** Same figure for both cycles (Free is $0 either way). null = no
   * listed price: the card says "Talk to us" instead (the custom plan). */
  price: number | { monthly: number; yearly: number } | null;
  cta: {
    label: string;
    /** A single href, or one per billing cycle. `null` means that cycle
     * has no Dodo product yet, which disables the button rather than
     * sending anyone to a checkout for a different price. */
    href: string | { monthly: string | null; yearly: string | null };
  };
  /** What Dodo actually charges per year: 12x `price.yearly` (the
   * discounted per-month rate). Shown alongside the
   * "/mo billed annually" figure so the real amount isn't a surprise at
   * the checkout, and it must match the Dodo product's own price exactly. */
  yearlyTotal?: number;
  /** Highlighted card border — the one visual nudge, since every button is now the same solid blue. */
  featured: boolean;
  limits: { label: string; value: string }[];
  /** This plan's own feature lines. With `inherits`, only what it adds on
   * top of that plan; the card lists everything, with these highlighted —
   * which keeps cards short on a phone, where they stack. */
  features: string[];
  /** The plan this one includes everything from (its id). */
  inherits?: string;
}

// Placeholder numbers/features per paid tier — invented to make the three
// cards actually look like three different plans rather than the same
// plan at three prices. Nothing here is enforced (see the file header's
// DISPLAY ONLY note); replace with the real tiers whenever they're decided.
export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    // /app requires sign-in, so this signs the visitor up, and a new
    // account's first visit to /app starts its trial (Dashboard.tsx's
    // StartingTrial). The limits below are enforced by
    // server/src/billing/entitlements.ts; keep them in step.
    cta: { label: 'Start free trial', href: '/app' },
    featured: false,
    limits: [
      { label: 'Trial length', value: '14 days' },
      { label: 'Messages (total)', value: '300' },
      { label: 'Chatbots', value: '1' },
      { label: 'Knowledge pages', value: '200' },
      { label: 'Branding', value: 'Shown' },
    ],
    // Chatbots and pages aren't repeated here: the Plan limits rows above
    // already say how many.
    features: [
      'Quick replies & forms',
      'Lead capture & email alerts',
      'Conversation logs',
      'Custom logo & colors',
      'Manual site rescan',
    ],
  },
  {
    // Shown as "Starter"; the id stays 'basic' for the same reason Business
    // keeps 'enterprise' (see below).
    id: 'basic',
    name: 'Starter',
    price: { monthly: 29, yearly: 19 },
    yearlyTotal: 228, // Dodo product pdt_0NpdjdeH… — $19 x 12
    cta: { label: 'Subscribe', href: { monthly: '/billing/checkout/basic/monthly', yearly: '/billing/checkout/basic/yearly' } },
    featured: false,
    limits: [
      { label: 'Messages / month', value: '1,000' },
      { label: 'Chatbots', value: '1' },
      { label: 'Knowledge pages', value: '500' },
      { label: 'Branding', value: 'Removable ($20/mo)' },
    ],
    inherits: 'free',
    features: ['Monthly auto-refresh'],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: { monthly: 79, yearly: 59 },
    yearlyTotal: 708, // Dodo product pdt_0Npdjtf2… — $59 x 12
    cta: { label: 'Subscribe', href: { monthly: '/billing/checkout/professional/monthly', yearly: '/billing/checkout/professional/yearly' } },
    featured: true,
    limits: [
      { label: 'Messages / month', value: '5,000' },
      { label: 'Chatbots', value: '2' },
      { label: 'Knowledge pages', value: '2,000' },
      { label: 'Branding', value: 'Hidden' },
    ],
    inherits: 'basic',
    features: ['Weekly auto-refresh', 'Unanswered-question reports'],
  },
  {
    // Shown as "Business"; the id stays 'enterprise' because it's the plan
    // name stored on accounts, in the checkout paths and on the Dodo
    // products (server/src/billing/index.ts) — renaming it would orphan
    // every existing subscriber.
    id: 'enterprise',
    name: 'Business',
    price: { monthly: 199, yearly: 149 },
    yearlyTotal: 1788, // Dodo product pdt_0Npdk4wM… — $149 x 12
    cta: { label: 'Subscribe', href: { monthly: '/billing/checkout/enterprise/monthly', yearly: '/billing/checkout/enterprise/yearly' } },
    featured: false,
    limits: [
      { label: 'Messages / month', value: '10,000' },
      { label: 'Chatbots', value: '3' },
      { label: 'Knowledge pages', value: '10,000' },
      { label: 'Branding', value: 'Hidden' },
    ],
    inherits: 'professional',
    features: ['Priority support'],
  },
  {
    // No price and no checkout: firms with several locations get a plan
    // set up with us by hand. The button emails sales, as the Contact
    // page's "Talk to sales" does.
    id: 'custom',
    name: 'Custom',
    price: null,
    cta: { label: 'Contact sales', href: 'mailto:sales@volgachat.com?subject=Multi-location%20enquiry' },
    featured: false,
    limits: [
      { label: 'Messages / month', value: 'Custom' },
      { label: 'Chatbots', value: 'Custom' },
      { label: 'Knowledge pages', value: 'Custom' },
      { label: 'Branding', value: 'Hidden' },
    ],
    // No feature list: what's included is agreed per customer, so the card
    // doesn't promise any particular set.
    features: [],
  },
];

/** The remove-badge add-on: takes "Powered by VolgaChat" off the chat
 * panel on Starter (Professional and Business never show it). Matches the
 * Starter card's "Branding: Removable ($20/mo)" row and its tooltip — see
 * server/src/billing/addons.ts. */
// Named as on the Dodo product, so checkout and receipts match the dashboard.
export const REMOVE_BADGE_ADDON = { name: 'Remove VolgaChat branding', monthly: 20 };

/** The plan a trial continues on: what the Free card's day-31 line quotes,
 * and where the dashboard's "Continue on Basic" button checks out. Looked
 * up from PLANS rather than restated, so its price can't drift. */
export const BASIC_PLAN = PLANS.find((p) => p.id === 'basic') as Plan;

export function priceFor(plan: Plan, billing: Billing): number | null {
  return plan.price === null || typeof plan.price === 'number' ? plan.price : plan.price[billing];
}

/** Dollars a year saved by paying yearly instead of monthly. */
function yearlySaving(plan: Plan): number {
  if (plan.price === null || typeof plan.price === 'number' || plan.yearlyTotal === undefined) return 0;
  return plan.price.monthly * 12 - plan.yearlyTotal;
}

/** The yearly toggle's "Save up to N%": the best saving on any paid card,
 * rounded down so the chip never promises more than a card delivers. */
function maxYearlySaving(): number {
  let best = 0;
  for (const plan of PLANS) {
    if (plan.price === null || typeof plan.price === 'number' || !plan.yearlyTotal) continue;
    best = Math.max(best, yearlySaving(plan) / (plan.price.monthly * 12));
  }
  return Math.floor(best * 100);
}

/** Lines that a later plan's line replaces rather than adds to. */
const SUPERSEDED_BY: Record<string, string> = { 'Monthly auto-refresh': 'Weekly auto-refresh' };

/** Every feature line a plan has, its inherited ones included. */
function allFeatures(plan: Plan): string[] {
  const parent = plan.inherits ? PLANS.find((p) => p.id === plan.inherits) : undefined;
  const lines = [...(parent ? allFeatures(parent) : []), ...plan.features];
  return lines.filter((line) => !SUPERSEDED_BY[line] || !lines.includes(SUPERSEDED_BY[line]));
}

/** What a card lists: "Everything in X, plus" and its own lines when X's
 * card is on screen to read, otherwise the whole list (Free is hidden for
 * a signed-in account, so Starter can't point at it). */
function cardFeatures(plan: Plan): Array<{ text: string; added: boolean }> {
  // Every feature the plan has, so each card stands on its own; the ones
  // it adds over the plan before it (its own `features`) are flagged, and
  // the card colours and bolds them. The base plan flags nothing — there's
  // nothing before it to add to.
  return allFeatures(plan).map((text) => ({ text, added: !!plan.inherits && plan.features.includes(text) }));
}

export function hrefFor(plan: Plan, billing: Billing): string | null {
  return typeof plan.cta.href === 'string' ? plan.cta.href : plan.cta.href[billing];
}

// ---- tooltips -------------------------------------------------------------
//
// Every limit row and feature line on a card explains itself: on hover or
// keyboard focus where there's a pointer, and on tap on a phone (tap again,
// tap elsewhere, or press Escape to close). Keyed by the exact text shown
// on the card, with patterns for the lines whose numbers differ by plan —
// so a line added to PLANS without an entry here simply has no tooltip
// rather than a wrong one.

const LIMIT_TIPS: Record<string, string> = {
  'Trial length': 'Free for 14 days from the moment you start, or until the trial messages run out, whichever comes first.',
  'Messages (total)':
    'Answers your assistant can give visitors during the whole trial. When they run out it pauses; nothing you set up is lost.',
  'Messages / month': 'Answers your assistant gives visitors each month. The count resets on the 1st.',
  Chatbots: 'Separate assistants, each with its own content, look and websites. One per site or brand.',
  'Knowledge pages':
    'Website pages you choose for your assistant to learn from, plus uploaded PDF, Word, text or Markdown files, across all your chatbots.',
};

const BRANDING_TIPS: Record<string, string> = {
  Shown: 'A small "Powered by VolgaChat" line appears under your chat.',
  'Removable ($20/mo)':
    '"Powered by VolgaChat" appears under your chat. Remove it with the $20/mo Remove branding add-on, from Billing in your dashboard.',
  Hidden: 'No VolgaChat branding anywhere in your chat.',
};

const FEATURE_TIPS: Record<string, string> = {
  'Quick replies & forms':
    "Clickable answer choices, suggested questions under the greeting, and forms that collect a visitor's name, email or phone.",
  'Lead capture & email alerts':
    'Contact details visitors leave in your chat are saved on your Leads page and emailed to you straight away, with a link to the conversation.',
  'Conversation logs':
    "Read every conversation in full, including contact details visitors leave. You choose how long they're kept, from 1 day to a year (90 days unless you change it).",
  'Custom logo & colors': 'Your brand colours and your own logo, or an animated chat bubble from the gallery.',
  'Manual site rescan': 'Re-read your website from the dashboard whenever it changes, so answers stay current.',
  'Weekly auto-refresh': 'Your website is re-read every week, so answers keep up with your pages. You can still rescan any time.',
  'Monthly auto-refresh':
    'Your website is re-read at the start of every month, so answers keep up with your pages. You can still rescan any time.',
  'Unanswered-question reports':
    "See what your assistant couldn't answer, with similar questions grouped together, so you can fill the gaps.",
  'Priority support': 'Your emails to our support team are answered first.',
};

function limitTip(label: string, value: string): string | undefined {
  return label === 'Branding' ? BRANDING_TIPS[value] : LIMIT_TIPS[label];
}

function featureTip(feature: string): string | undefined {
  if (/^\d[\d,]* chatbots?$/.test(feature)) return LIMIT_TIPS.Chatbots;
  if (/^Up to [\d,]+ pages$/.test(feature)) return LIMIT_TIPS['Knowledge pages'];
  return FEATURE_TIPS[feature];
}

/** A card line with its explanation. A real button, so it's reachable by
 * keyboard and announced by screen readers (aria-describedby); the bubble
 * itself is CSS-positioned under the text. */
function Tip({
  id,
  tip,
  open,
  setOpen,
  children,
}: {
  id: string;
  tip: string | undefined;
  open: boolean;
  setOpen: (id: string | null) => void;
  children: preact.ComponentChildren;
}) {
  if (!tip) return <>{children}</>;
  return (
    <span class={`pc-tip${open ? ' pc-tip-open' : ''}`}>
      <button
        type="button"
        class="pc-tip-trigger"
        aria-describedby={`${id}-tip`}
        aria-expanded={open}
        onClick={() => setOpen(open ? null : id)}
      >
        {children}
      </button>
      <span class="pc-tip-bubble" role="tooltip" id={`${id}-tip`}>
        {tip}
      </span>
    </span>
  );
}

/** Which tooltip a tap opened: one at a time, closed by a tap anywhere
 * else or by Escape. Hover and keyboard focus need no state (CSS). */
function useOpenTip(): [string | null, (id: string | null) => void] {
  const [openTip, setOpenTip] = useState<string | null>(null);
  useEffect(() => {
    if (!openTip) return;
    const onPointer = (e: Event) => {
      if (!(e.target instanceof Element) || !e.target.closest('.pc-tip')) setOpenTip(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenTip(null);
    };
    document.addEventListener('click', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [openTip]);
  return [openTip, setOpenTip];
}

export function BillingToggle({ billing, setBilling }: { billing: Billing; setBilling: (b: Billing) => void }) {
  return (
    <div class="pc-toggle" role="radiogroup" aria-label="Billing cycle">
      <button
        type="button"
        role="radio"
        aria-checked={billing === 'monthly'}
        class={`pc-toggle-btn${billing === 'monthly' ? ' pc-toggle-btn-active' : ''}`}
        onClick={() => setBilling('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={billing === 'yearly'}
        class={`pc-toggle-btn${billing === 'yearly' ? ' pc-toggle-btn-active' : ''}`}
        onClick={() => setBilling('yearly')}
      >
        Yearly
      </button>
      {/* The actual offer, not decoration: the best saving on any plan
          (Starter: $228 a year against $348 paid monthly). */}
      <span class="pc-toggle-chip">Save up to {maxYearlySaving()}%</span>
    </div>
  );
}

/** The self-serve paid tiers, cheapest first — what "upgrade" and
 * "downgrade" are measured against. */
export const PAID_TIER_ORDER = ['basic', 'professional', 'enterprise'] as const;

/** The plan a signed-in customer pays for now, and how it's billed (null
 * when not known yet — the cycle comes from the payment provider). */
export interface CurrentPlan {
  plan: string;
  cycle: Billing | null;
}

/** What a card's button says for a customer already on a plan: the usual
 * pricing-page states — their own plan (not a purchase), the same plan on
 * the other cycle, and up or down from where they are. Null: nothing
 * special, use the card's own label. */
function ctaStateFor(plan: Plan, billing: Billing, current: CurrentPlan | undefined): 'current' | 'switch' | 'upgrade' | 'downgrade' | null {
  if (!current || plan.price === null || plan.id === 'free') return null;
  if (plan.id === current.plan) return current.cycle && current.cycle !== billing ? 'switch' : 'current';
  const from = (PAID_TIER_ORDER as readonly string[]).indexOf(current.plan);
  const to = (PAID_TIER_ORDER as readonly string[]).indexOf(plan.id);
  if (from === -1 || to === -1) return null;
  return to > from ? 'upgrade' : 'downgrade';
}

export function PlanCards({
  plans,
  billing,
  current,
}: {
  plans: Plan[];
  billing: Billing;
  /** Signed in and paying: marks their plan and labels the others as
   * upgrades and downgrades (Pricing.tsx). */
  current?: CurrentPlan;
}) {
  const [openTip, setOpenTip] = useOpenTip();
  return (
    <div class={`pc-cards pc-cards-${plans.length}`}>
      {plans.map((plan) => (
        <div
          class={`pc-summary pc-summary-${plan.id}${plan.featured ? ' pc-summary-featured' : ''}${current?.plan === plan.id ? ' pc-summary-current' : ''}`}
          key={plan.id}
        >
          <div class="pc-summary-head">
            <h3>{plan.name}</h3>
            {current?.plan === plan.id && <span class="pc-current-badge">Current plan</span>}
          </div>

          {plan.price === null ? (
            <p class="pc-price">
              <span class="pc-price-amount pc-price-talk">Talk to us</span>
            </p>
          ) : (
            <p class="pc-price">
              <span class="pc-price-amount">${priceFor(plan, billing)}</span>
              {/* Free says "for 14 days" right next to the price, not buried
                  in the limits list below — the headline should set the
                  expectation, not contradict it. */}
              <span class="pc-price-unit">{plan.id === 'free' ? 'for 14 days' : '/mo'}</span>
            </p>
          )}
          <p class="pc-price-note">
            {plan.id === 'free'
              ? 'No credit card required'
              : plan.price === null
                ? 'For firms with several locations'
                : billing === 'yearly'
                  ? // The amount Dodo will actually charge, spelled out — the
                    // big number above is the per-month equivalent, and being
                    // vague about the real total is how checkout surprises
                    // happen.
                    `$${(plan.yearlyTotal ?? 0).toLocaleString('en-US')} billed yearly — save $${yearlySaving(plan).toLocaleString('en-US')}`
                  : 'Billed monthly, cancel anytime'}
          </p>

          {/* Above the details, not below them — a visitor shouldn't have
              to scroll a whole feature list to find the button. Solid blue
              on every card, not just the featured one: a row of mismatched
              ghost/solid buttons reads as "some of these aren't real
              options." Professional keeps its highlighted border above as
              the one visual nudge. */}
          {ctaStateFor(plan, billing, current) === 'current' ? (
            // Their own plan isn't something to buy again: say so, rather
            // than offer a Subscribe that only lands on "nothing to change".
            <button type="button" class="site-btn pc-cta pc-cta-current" disabled>
              Your current plan
            </button>
          ) : hrefFor(plan, billing) ? (
            // For a subscriber, the same link goes through the Billing
            // page's change-plan confirmation (routes/billing.ts), so the
            // label says what it'll do there.
            <a href={hrefFor(plan, billing) as string} class={`site-btn pc-cta ${ctaStateFor(plan, billing, current) === 'downgrade' ? 'site-btn-secondary' : 'site-btn-primary'}`}>
              {{
                switch: `Switch to ${billing}`,
                // The card already names the plan; "Upgrade to Professional"
                // wrapped onto two lines and broke the row of buttons.
                upgrade: 'Upgrade',
                downgrade: 'Downgrade',
                current: plan.cta.label,
              }[ctaStateFor(plan, billing, current) ?? 'current']}
            </a>
          ) : (
            // No Dodo product for this cycle yet. Disabled on purpose:
            // sending someone from a "$15/mo" card into a checkout that
            // charges a different amount is worse than a button that
            // plainly isn't ready.
            <>
              <button type="button" class="site-btn site-btn-primary pc-cta" disabled>
                {plan.cta.label}
              </button>
              <p class="pc-cta-note">
                {billing === 'monthly'
                  ? 'Monthly billing is coming soon — switch to Yearly to subscribe today.'
                  : 'Yearly billing is coming soon — switch to Monthly to subscribe today.'}
              </p>
            </>
          )}

          <div class="pc-summary-group">
            <p class="pc-summary-label">Plan limits</p>
            <dl class="pc-rows">
              {plan.limits.map((l) => {
                const id = `pc-${plan.id}-limit-${l.label.replace(/\W+/g, '-').toLowerCase()}`;
                return (
                  <div key={l.label}>
                    <dt>
                      <Tip id={id} tip={limitTip(l.label, l.value)} open={openTip === id} setOpen={setOpenTip}>
                        {l.label}
                      </Tip>
                    </dt>
                    <dd>{l.value}</dd>
                  </div>
                );
              })}
            </dl>
          </div>

          {(() => {
            const lines = cardFeatures(plan);
            if (lines.length === 0) return null;
            return (
              <div class="pc-summary-group">
                <p class="pc-summary-label">What's included</p>
                <ul class="site-pricing-features pc-features">
                  {lines.map((f, i) => {
                    const id = `pc-${plan.id}-feature-${i}`;
                    return (
                      <li key={f.text} class={f.added ? 'pc-feature-added' : undefined}>
                        <Tip id={id} tip={featureTip(f.text)} open={openTip === id} setOpen={setOpenTip}>
                          {f.text}
                        </Tip>
                        {/* Colour and weight aren't announced; this is. */}
                        {f.added && <span class="pc-sr-only"> — new on this plan</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })()}

          {/* What happens on day 31 — without it the Free card is a dead
              end, and people hesitate to start a trial when they can't tell
              whether their work disappears at the end of it. Nothing is
              deleted: the bot pauses until they pick a plan (see
              server/src/billing/entitlements.ts), and the trial-ended
              banner offers Starter in one click. */}
          {plan.id === 'free' && (
            <p class="pc-card-footnote">
              Then continue on Starter from ${priceFor(BASIC_PLAN, billing)}/mo — your bot and content are kept.
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
